import { calculateAttorneyFee } from "@/lib/utils/attorneyFeeUtils";
import { formatAttorneyFee } from "@/lib/utils/attorneyFeeUtils";
import { TrustCheckCard } from "@/components/features/matter/trust-checks/TrustCheckCard";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CurrencyWrapper,
  ServerErrorFallbackCard,
  Text,
} from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useUser } from "@/components/UserProvider";
import type { SettlementViewModel } from "@/hooks/matter-view-models/use-settlements-view-model";
import { useTrustChecksViewModel } from "@/hooks/matter-view-models/use-trust-checks-view-model";
import { useFormatInsurancePolicyLimits } from "@/hooks/useFormatInsurancePolicyLimits";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type Matter, type MatterInsurance } from "@/lib/types";
import { getInsuranceContactConnection } from "@/lib/utils/connections/insurance-contact-connections";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { formatInsuranceTitle } from "@/lib/utils/format";
import { SettlementIcon, TrustCheckIcon } from "@/lib/utils/iconUtils";
import { ErrorBoundary } from "@sentry/react";
import { orderBy } from "lodash";
import { ChevronRight, CircleCheck, CircleHelp, PlusIcon } from "lucide-react";
import pluralize from "pluralize";
import { useState } from "react";
import { NegotiationCard } from "./NegotiationCard";
interface SettlementsDemandCardProps {
  demands: SettlementViewModel[];
  insurance?: MatterInsurance;
  matter: Matter;
  isMedpay?: boolean;
  coverageType?: string;
}

const SettlementsDemandCardDangerous = ({
  demands,
  insurance,
  matter,
  isMedpay = false,
  coverageType,
}: SettlementsDemandCardProps) => {
  const { hasActionPermission } = useUser();
  const {
    openSettlementDemandModal,
    openSettlementCounterModal,
    openTrustCheckModal,
  } = useGlobalModal();
  useGlobalModal();
  const formatInsurancePolicyLimits = useFormatInsurancePolicyLimits();
  const insuranceTitle = formatInsuranceTitle({
    insurance: insurance,
  });
  const groupCoverageType = coverageType ?? (isMedpay ? "MedPay" : undefined);
  const insuredConnection = getInsuranceContactConnection(insurance, "Insured");
  const clientConnection = getMatterContactConnections(matter).find(
    (connection) => connection.label === "Client",
  );
  const isFirstPartyInsurance =
    !!insuredConnection?.targetObjectId &&
    insuredConnection.targetObjectId === clientConnection?.contactId;
  const showUm =
    isFirstPartyInsurance &&
    (insurance?.umExists ||
      insurance?.umPerPerson != null ||
      insurance?.umPerIncident != null);
  const showUim =
    isFirstPartyInsurance &&
    (insurance?.uimExists ||
      insurance?.uimPerPerson != null ||
      insurance?.uimPerIncident != null);
  const showMedPay =
    isFirstPartyInsurance &&
    (insurance?.medPay || insurance?.medPayLimit != null);

  const sortedDemands = orderBy(
    demands,
    (demand) => {
      const date =
        demand.type === "Offer" ? demand.dateReceived : demand.dateSent;
      const t = date ? Date.parse(date) : NaN;
      return Number.isNaN(t) ? 0 : t;
    },
    ["desc"],
  );

  const acceptedSettlement = sortedDemands.find((demand) => {
    if (demand.type === "Offer") {
      return demand.accepted;
    }
    if (demand.type === "Counter") {
      return demand.accepted;
    }
    return demand.coverageType === "MedPay" && demand.accepted;
  });
  const isAcceptedOffer = acceptedSettlement?.type === "Offer";
  const isAcceptedCounter = acceptedSettlement?.type === "Counter";
  const isAcceptedMedPayDemand =
    acceptedSettlement?.type === "Demand" &&
    acceptedSettlement.coverageType === "MedPay";
  const acceptedSettlementId = acceptedSettlement?.id;
  const matterId = matter.id;
  const trustChecksViewModel = useTrustChecksViewModel(matterId);
  const sortedTrustChecks =
    acceptedSettlementId &&
    (isAcceptedOffer || isAcceptedCounter || isAcceptedMedPayDemand)
      ? trustChecksViewModel.filter((trustCheck) =>
          trustCheck.connections.some((connection) =>
            isAcceptedMedPayDemand
              ? connection.settlementDemandId === acceptedSettlementId
              : isAcceptedCounter
                ? connection.settlementCounterId === acceptedSettlementId
                : connection.settlementOfferId === acceptedSettlementId,
          ),
        )
      : [];
  const trustCheckSettlementPayload =
    acceptedSettlementId && isAcceptedMedPayDemand
      ? { settlementDemandId: acceptedSettlementId }
      : acceptedSettlementId && isAcceptedCounter
        ? { settlementCounterId: acceptedSettlementId }
        : acceptedSettlementId && isAcceptedOffer
          ? { settlementOfferId: acceptedSettlementId }
          : {};
  const [expanded, setExpanded] = useState<"negotiations" | "none">(() =>
    acceptedSettlement ? "none" : "negotiations",
  );
  const [expandedTrustChecks, setExpandedTrustChecks] = useState<
    "trust-checks" | "none"
  >("none");

  const totalDeposits = sortedTrustChecks.reduce(
    (acc, trustCheck) =>
      acc +
      (trustCheck.type === "Deposit"
        ? (trustCheck.amount ?? 0)
        : (trustCheck.amount ?? 0) * -1), // negative for withdrawals
    0,
  );

  const isExpanded = expanded === "negotiations";
  const isExpandedTrustChecks = expandedTrustChecks === "trust-checks";
  const hasDemandOrOffer = sortedDemands.some(
    (demand) => demand.type === "Offer" || demand.type === "Demand",
  );
  return (
    <Card key={insurance?.id} className="border-border/50 w-full gap-y-1">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-x-2">
          <Text
            variant="cardTitle"
            color="primary"
            className="mb-1.5 flex flex-1 flex-wrap items-center gap-x-2"
          >
            {insuranceTitle?.insurerPart} - {insuranceTitle?.insuredPart} -{" "}
            {insuranceTitle?.insuranceTypePart}
            {groupCoverageType && (
              <Badge variant="outline">{groupCoverageType}</Badge>
            )}
          </Text>
        </div>
        <div className="mt-1 flex items-center gap-x-2">
          <Text variant="subHeading">
            Policy: {insurance?.policyNumber || "-"} | Claim:{" "}
            {insurance?.claimNumber || "-"}
          </Text>
        </div>
        {!isFirstPartyInsurance && (
          <div className="mt-1 flex items-center gap-x-2">
            <Text variant="caption" color="muted">
              Policy limits: {formatInsurancePolicyLimits(insurance)}
            </Text>
            {insurance?.policyLimitConfirmation ? (
              <TooltipWrapper message="Policy limits confirmed">
                <CircleCheck className="h-5 w-5 text-green-600" />
              </TooltipWrapper>
            ) : (
              <TooltipWrapper message="Policy limits not confirmed">
                <CircleHelp className="h-5 w-5 text-yellow-600" />
              </TooltipWrapper>
            )}
          </div>
        )}
        {isFirstPartyInsurance && (
          <div className="mt-1 flex flex-col gap-1">
            {showMedPay && (
              <Text
                variant="caption"
                color="muted"
                className="flex items-center gap-1"
              >
                MedPay:
                <CurrencyWrapper value={insurance.medPayLimit ?? undefined} />
              </Text>
            )}
            {showUm && (
              <Text
                variant="caption"
                color="muted"
                className="flex items-center gap-1"
              >
                UM:
                <CurrencyWrapper value={insurance.umPerPerson ?? undefined} />
                /
                <CurrencyWrapper value={insurance.umPerIncident ?? undefined} />
              </Text>
            )}
            {showUim && (
              <Text
                variant="caption"
                color="muted"
                className="flex items-center gap-1"
              >
                UIM:
                <CurrencyWrapper value={insurance.uimPerPerson ?? undefined} />
                /
                <CurrencyWrapper
                  value={insurance.uimPerIncident ?? undefined}
                />
              </Text>
            )}
          </div>
        )}
      </CardHeader>
      {acceptedSettlement && (
        <div className="px-6 py-4">
          <div className="grid grid-cols-5 gap-4">
            <div>
              <Text variant="subHeading" className="font-medium">
                Settlement amount
              </Text>
              <Text variant="body" className="text-lg leading-7 font-semibold">
                <CurrencyWrapper
                  value={acceptedSettlement.netSettlementAmount}
                />
              </Text>
            </div>
            <div>
              <Text variant="subHeading" className="font-medium">
                Fee %
              </Text>
              <Text variant="body" className="text-lg leading-7 font-semibold">
                {formatAttorneyFee(acceptedSettlement.attorneyFeePercentage)}%
              </Text>
            </div>
            <div>
              <Text variant="subHeading" className="font-medium">
                Fee amount
              </Text>
              <Text variant="body" className="text-lg leading-7 font-semibold">
                <CurrencyWrapper
                  value={calculateAttorneyFee(
                    acceptedSettlement.netSettlementAmount,
                    acceptedSettlement.attorneyFeePercentage,
                  )}
                  showCents
                />
              </Text>
            </div>
            <div>
              <Text variant="subHeading" className="font-medium">
                Deposited
              </Text>
              <Text variant="body" className="text-lg leading-7 font-semibold">
                <CurrencyWrapper value={totalDeposits} />
              </Text>
            </div>
            <div>
              <Text variant="subHeading" className="font-medium">
                Remaining
              </Text>
              <Text variant="body" className="text-lg leading-7 font-semibold">
                <CurrencyWrapper
                  value={acceptedSettlement.netSettlementAmount - totalDeposits}
                />
              </Text>
            </div>
          </div>
        </div>
      )}
      <CardContent className={acceptedSettlement ? "pt-0 pb-4" : "pb-4"}>
        <Accordion
          type="single"
          collapsible
          defaultValue="negotiations"
          value={expanded}
          onValueChange={() => {
            setExpanded((prev) =>
              prev === "negotiations" ? "none" : "negotiations",
            );
          }}
        >
          <AccordionItem
            value="negotiations"
            className="bg-card border-border mb-2 w-full rounded-lg border shadow-sm"
          >
            <div className="hover:bg-light-primary/50 flex w-full cursor-pointer items-center transition-colors">
              <AccordionTrigger
                className="min-w-0 flex-1 cursor-pointer px-4 py-3 hover:bg-transparent hover:no-underline [&[data-state=open]_.negotiations-chevron]:rotate-90"
                icon={<></>}
              >
                <div className="flex min-w-0 flex-1 items-center gap-3 pr-2 text-left">
                  <ChevronRight
                    data-testid="negotiations-accordion-trigger-icon"
                    className="negotiations-chevron text-muted-foreground h-4 w-4 shrink-0 transition-transform duration-200"
                    aria-hidden
                  />
                  <SettlementIcon className="text-primary h-4 w-4 shrink-0" />
                  <Text variant="body" className="font-semibold">
                    Negotiations
                  </Text>
                  {!isExpanded && (
                    <Text variant="subHeading" className="line-clamp-1">
                      {pluralize("negotiation", sortedDemands.length, true)}
                    </Text>
                  )}
                </div>
                {hasActionPermission(
                  ACTION_PERMISSION_KEYS.matterSettlementDemand,
                  ACTION_PERMISSION_VALUES.create,
                ) &&
                  isExpanded && (
                    <div className="mr-4 flex items-center gap-x-2">
                      <Button
                        size="sm"
                        type="button"
                        variant="outline"
                        className="bg-background hover:bg-accent hover:text-accent-foreground"
                        onClick={(e) => {
                          e.preventDefault();
                          openSettlementDemandModal({
                            matterId,
                            settlementId: insurance?.id,
                            negotiationType: "Demand",
                            editing: false,
                            initialTab: "details",
                            selectedInsurance: insurance,
                            isMedpay: isMedpay,
                            coverageType: groupCoverageType,
                          });
                        }}
                      >
                        <PlusIcon className="mr-2 h-4 w-4" />
                        Demand
                      </Button>
                      <Button
                        size="sm"
                        type="button"
                        variant="outline"
                        className="bg-background hover:bg-accent hover:text-accent-foreground"
                        onClick={(e) => {
                          e.preventDefault();
                          openSettlementDemandModal({
                            matterId,
                            settlementId: insurance?.id,
                            negotiationType: "Offer",
                            editing: false,
                            initialTab: "details",
                            selectedInsurance: insurance,
                            isMedpay: isMedpay,
                            coverageType: groupCoverageType,
                          });
                        }}
                      >
                        <PlusIcon className="mr-2 h-4 w-4" />
                        Offer
                      </Button>
                      {hasDemandOrOffer ? (
                        <Button
                          data-testid="create-counter-button"
                          size="sm"
                          type="button"
                          variant="outline"
                          className="bg-background hover:bg-accent hover:text-accent-foreground"
                          onClick={(e) => {
                            e.preventDefault();
                            openSettlementCounterModal({
                              matterId,
                              settlementCounterId: undefined,
                              editing: false,
                              initialTab: "details",
                              selectedInsurance: insurance,
                              coverageType: groupCoverageType,
                            });
                          }}
                        >
                          <PlusIcon className="mr-2 h-4 w-4" />
                          Counter
                        </Button>
                      ) : (
                        <TooltipWrapper message="Add a demand or offer before creating a counter.">
                          <span className="inline-flex">
                            <Button
                              size="sm"
                              type="button"
                              variant="outline"
                              className="bg-background"
                              disabled
                            >
                              <PlusIcon className="mr-2 h-4 w-4" />
                              Counter
                            </Button>
                          </span>
                        </TooltipWrapper>
                      )}
                    </div>
                  )}
              </AccordionTrigger>
            </div>
            <AccordionContent className="px-4 py-6">
              <div className="grid grid-cols-1 gap-4">
                {sortedDemands.map((negotiation) => (
                  <NegotiationCard
                    key={negotiation.id}
                    matter={matter}
                    negotiation={negotiation}
                    onEdit={(initialTab) => {
                      if (negotiation.type === "Counter") {
                        openSettlementCounterModal({
                          matterId,
                          settlementCounterId: negotiation.id,
                          editing: true,
                          initialTab: initialTab ?? "details",
                          selectedInsurance: insurance,
                          coverageType: groupCoverageType,
                        });
                        return;
                      }
                      openSettlementDemandModal({
                        matterId,
                        settlementId: negotiation.id,
                        negotiationType:
                          negotiation.type === "Offer" ? "Offer" : "Demand",
                        editing: true,
                        initialTab: initialTab ?? "details",
                        selectedInsurance: insurance,
                        isMedpay:
                          negotiation.type === "Offer"
                            ? (negotiation.medPay ?? false)
                            : negotiation.coverageType === "MedPay",
                      });
                    }}
                  />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
      <CardContent className="pt-0 pb-4">
        <Accordion
          type="single"
          collapsible
          defaultValue="trust-checks"
          value={expandedTrustChecks}
          onValueChange={() => {
            setExpandedTrustChecks((prev) =>
              prev === "trust-checks" ? "none" : "trust-checks",
            );
          }}
        >
          <AccordionItem
            value="trust-checks"
            className="bg-card border-border w-full rounded-lg border shadow-sm"
          >
            <div className="hover:bg-light-primary/50 flex w-full cursor-pointer items-center transition-colors">
              <AccordionTrigger
                className="min-w-0 flex-1 cursor-pointer px-4 py-3 hover:bg-transparent hover:no-underline [&[data-state=open]_.trust-checks-chevron]:rotate-90"
                icon={<></>}
              >
                <div className="flex min-w-0 flex-1 items-center gap-3 pr-2 text-left">
                  <ChevronRight
                    className="trust-checks-chevron text-muted-foreground h-4 w-4 shrink-0 transition-transform duration-200"
                    aria-hidden
                  />
                  <TrustCheckIcon className="h-4 w-4 shrink-0 text-green-600" />
                  <Text variant="body" className="font-semibold">
                    Deposits and withdrawals
                  </Text>
                  {!isExpandedTrustChecks && (
                    <Text variant="subHeading" className="line-clamp-1">
                      {pluralize("transaction", sortedTrustChecks.length, true)}
                    </Text>
                  )}
                </div>
                {hasActionPermission(
                  ACTION_PERMISSION_KEYS.matterTrustCheck,
                  ACTION_PERMISSION_VALUES.create,
                ) &&
                  (isAcceptedOffer ||
                    isAcceptedCounter ||
                    isAcceptedMedPayDemand) &&
                  isExpandedTrustChecks && (
                    <div className="mr-4 flex items-center gap-x-2">
                      <Button
                        size="sm"
                        type="button"
                        variant="outline"
                        className="bg-background hover:bg-accent hover:text-accent-foreground"
                        onClick={(e) => {
                          e.preventDefault();
                          openTrustCheckModal({
                            matterId,
                            trustCheckId: undefined,
                            trustCheckType: "Deposit",
                            ...trustCheckSettlementPayload,
                          });
                        }}
                      >
                        <PlusIcon className="mr-2 h-4 w-4" />
                        Deposit
                      </Button>
                      <Button
                        size="sm"
                        type="button"
                        variant="outline"
                        className="bg-background hover:bg-accent hover:text-accent-foreground"
                        onClick={(e) => {
                          e.preventDefault();
                          openTrustCheckModal({
                            matterId,
                            trustCheckId: undefined,
                            trustCheckType: "Withdraw",
                            ...trustCheckSettlementPayload,
                          });
                        }}
                      >
                        <PlusIcon className="mr-2 h-4 w-4" />
                        Withdraw
                      </Button>
                    </div>
                  )}
                {!isAcceptedOffer &&
                  !isAcceptedCounter &&
                  !isAcceptedMedPayDemand &&
                  isExpandedTrustChecks && (
                    <div className="mr-4 flex items-center gap-x-2">
                      <Text variant="subHeading">
                        {acceptedSettlement
                          ? "Deposits and withdrawals are only available for accepted offers, counters, or MedPay demands."
                          : "Accept an offer, counter, or MedPay demand to create a deposit or withdrawal"}
                      </Text>
                    </div>
                  )}
              </AccordionTrigger>
            </div>
            <AccordionContent className="px-4 py-6">
              <div className="grid grid-cols-1 gap-4">
                {sortedTrustChecks.length === 0 && (
                  <Text variant="subHeading">No transactions found</Text>
                )}
                {sortedTrustChecks.map((trustCheck) => (
                  <TrustCheckCard
                    key={trustCheck.id}
                    trustCheck={trustCheck}
                    matter={matter}
                    className="border-none"
                  />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export const SettlementsDemandCard = (props: SettlementsDemandCardProps) => {
  return (
    <ErrorBoundary
      fallback={
        <div className="p-4">
          <ServerErrorFallbackCard message="Unable to load settlement details." />
        </div>
      }
    >
      <SettlementsDemandCardDangerous {...props} />
    </ErrorBoundary>
  );
};
